'use client';

import type { HpSearchScopeDto } from '@zg-rentals/ts-hotpads-api-client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';

import { css } from '@/styled-system/css';
import { Text } from '@zillow/constellation-10';
import type { SearchListingsRequest, SearchListingsResult } from '@/src/lib/api';
import { useSearchListings } from '@/src/lib/hooks/query';
import { useSrpLiveSearchRequest } from './useSrpLiveSearchRequest';
import { useOptionalFilters } from '@/src/lib/filters/useFilters';
import { SearchResultsStatus } from './SearchResultsStatus';

const countTextClass = css({
  minWidth: 0,
  whiteSpace: 'nowrap',
});

type Props = {
  initialListingsData: SearchListingsResult | null;
  initialParams: SearchListingsRequest;
  scope?: HpSearchScopeDto;
  page: number;
  propertyType: string;
};

function LoadingCount() {
  const t = useTranslations('search');
  return (
    <Text asChild textStyle="body-sm" color="text.subtle">
      <span aria-label={t('loadingRentalListingsAria')} className={countTextClass}>
        {t('loadingListings')}
      </span>
    </Text>
  );
}

function CountText({ totalRentals, propertyType }: { totalRentals: number; propertyType: string }) {
  const t = useTranslations('search');
  if (totalRentals <= 0) {
    return (
      <Text textStyle="body-sm" color="text.subtle">
        {t('noPropertyTypeFound', { propertyType: propertyType.toLowerCase() })}
      </Text>
    );
  }

  return (
    <Text asChild textStyle="body-sm">
      <span data-testid="srp-count" className={countTextClass}>
        {totalRentals.toLocaleString()} {t('rentalsAvailableLabel')}
      </span>
    </Text>
  );
}

/**
 * Live counts line. Reads the same live V3 search query LiveListingsGrid drives,
 * so the loading text shows during pan/zoom/filter refetches, not just initial SSR.
 */
export function CountsLine({ initialListingsData, initialParams, scope, page, propertyType }: Props) {
  const filterInteractionSeq = useOptionalFilters()?.interactionSeq ?? 0;
  const [hasHydrated, setHasHydrated] = useState(false);

  useEffect(() => {
    setHasHydrated(true);
  }, []);

  const liveParams = useSrpLiveSearchRequest({ initialParams, scope, page });
  const isInitialParams = liveParams === initialParams;

  const { data, isFetching, isPlaceholderData, isError, fetchStatus } = useSearchListings(liveParams, {
    refetchOnMount: false,
    initialData: isInitialParams ? (initialListingsData ?? undefined) : undefined,
  });

  const effectiveData = isInitialParams ? (data ?? initialListingsData ?? undefined) : (data ?? undefined);
  const totalRentals = effectiveData?.listings?.numUnits ?? effectiveData?.listings?.numBuildingsAvailable ?? 0;
  const isWaitingForLiveParamsData = !isInitialParams && (isPlaceholderData || data === undefined);

  // Only show the skeleton before we have any usable count. Once data has
  // landed (from SSR seed or a prior fetch), keep the number visible while
  // a background refetch runs — otherwise the map settling on its first
  // idle triggers a skeleton flash on top of the already-resolved count.
  const hasUsableData = effectiveData !== undefined;

  const showLoading =
    isWaitingForLiveParamsData || (hasHydrated && (isFetching || isPlaceholderData) && !hasUsableData);

  return (
    <>
      <SearchResultsStatus
        count={!isError && !isPlaceholderData && hasUsableData ? totalRentals : null}
        isSearching={!isError && (isFetching || fetchStatus === 'paused')}
        filterInteractionSeq={filterInteractionSeq}
      />
      {showLoading ? <LoadingCount /> : <CountText totalRentals={totalRentals} propertyType={propertyType} />}
    </>
  );
}
